import { useCallback } from 'react';
import { useAutomationsApiFacade } from '../../apis/facades/useAutomationsApiFacade';
import { useDispatch, useSelector } from 'react-redux';
import {
  requestRefreshGlobal,
  deleteAlert as deleteAlertAction,
  toggleAlert as toggleAlertAction,
  updateAlert,
  updateAlertDeployStatus,
  addAlert,
} from '../../reducers/global/globalReducer';
import { selectDefaultCreationEnvironment } from '../pages/environments/environmentsReducer';
import { DeployStatusForTable } from '../../types/enums/DeployStatusForTable';
import { getEditOptimisticStatus } from '../pages/helper';
import { useSweepNotifications } from '../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../reducers/notificationsReducer';
import { FailedAction } from '../../types/enums/FailedAction';
import { telemetry } from '../../telemetry';
import { useTrackInHubspot } from '../../hooks/useTrackInHubspot';
import { DEPLOY_TYPE_VALUES } from '../../constants/deployTypeValues';

const useAlerts = () => {
  const {
    create_automation,
    update_automation,
    toggle_automation,
    delete_automation,
    deploy_automation,
  } = useAutomationsApiFacade();
  const crmOrg = useSelector(selectDefaultCreationEnvironment);
  const crmOrgId = crmOrg?.id;
  const dispatch = useDispatch();
  const { addNotification } = useSweepNotifications();
  const { trackDeployment } = useTrackInHubspot();

  const handleError = useCallback(
    (e: any, failedAction: FailedAction = FailedAction.deploy): ErrorInApi => {
      addNotification({
        message: `Failed to ${failedAction} due to technical issues`,
        keepOpen: true,
        details: e.response.data.message,
        variant: SweepNotificationVariant.Error,
      });
      telemetry.captureError(e);
      return { isError: true };
    },
    [addNotification],
  );

  const deployOrRefresh = useCallback(
    ({
      deployToOrgIds,
      deployAutomationDto,
    }: {
      deployToOrgIds: string[];
      deployAutomationDto: AutomationStructureNew;
    }) => {
      if (deployToOrgIds.length > 0) {
        deploy_automation({
          automationId: deployAutomationDto.automationId,
          versionId: deployAutomationDto.versionId,
          type: deployAutomationDto.type,
          deployToOrgIds,
        });
        trackDeployment(DEPLOY_TYPE_VALUES.ALERTS);
        //when deploy is finished, a web socket will be sent in order to trigger "requestRefresh"
      } else if (crmOrgId) {
        dispatch(requestRefreshGlobal({ crmOrgId }));
      }
    },
    [crmOrgId, deploy_automation, dispatch, trackDeployment],
  );

  const createAlert = useCallback(
    async ({
      deployToOrgIds,
      createAutomationDto,
    }: {
      deployToOrgIds: string[];
      createAutomationDto: AutomationStructureNew;
    }) => {
      try {
        const toSend = {
          ...createAutomationDto,
          deployToOrgIds,
        };
        const newAutomation = await create_automation(toSend);
        const shouldDeploy = deployToOrgIds.length > 0;
        if (crmOrgId) {
          dispatch(
            addAlert({
              crmOrgId,
              automation: newAutomation,
              optimisticStatus: shouldDeploy
                ? DeployStatusForTable.Deployed
                : DeployStatusForTable.Draft,
            }),
          );
        }
        deployOrRefresh({
          deployToOrgIds,
          deployAutomationDto: newAutomation,
        });
        return newAutomation;
      } catch (e) {
        return handleError(e, FailedAction.create);
      }
    },
    [create_automation, crmOrgId, deployOrRefresh, dispatch, handleError],
  );

  const editAlert = useCallback(
    async ({
      deployToOrgIds,
      updateAutomationDto,
      versionId,
    }: {
      updateAutomationDto: AutomationStructureNew;
      deployToOrgIds: string[];
      versionId: string;
    }) => {
      try {
        const toSend = {
          ...updateAutomationDto,
          deployToOrgIds,
        };
        const updatedAutomation = await update_automation(toSend);
        const shouldDeploy = deployToOrgIds.length > 0;
        if (crmOrgId) {
          dispatch(
            updateAlert({
              crmOrgId,
              automationToUpdate: updatedAutomation,
              versionIdToUpdate: versionId, //updatedAutomation.versionId ? because "updatedRollup" has a new versionId, that doesn't exist yet in the store
              optimisticStatus: shouldDeploy
                ? DeployStatusForTable.Deployed
                : getEditOptimisticStatus(updateAutomationDto.status),
            }),
          );
        }
        deployOrRefresh({
          deployToOrgIds,
          deployAutomationDto: updatedAutomation,
        });
        return updatedAutomation;
      } catch (e) {
        return handleError(e, FailedAction.save);
      }
    },
    [update_automation, crmOrgId, deployOrRefresh, dispatch, handleError],
  );

  const deleteAlert = useCallback(
    async ({
      automationId,
      automationType,
    }: {
      automationId: string;
      automationType: AutomationType;
    }) => {
      try {
        await delete_automation(automationId, automationType);
        if (crmOrgId) {
          dispatch(
            deleteAlertAction({
              crmOrgId,
              automationIdToDelete: automationId,
            }),
          );
        }
        //when deploy is finished, a web socket will be sent in order to trigger "requestRefresh"
      } catch (e) {
        return handleError(e, FailedAction.delete);
      }
    },
    [delete_automation, crmOrgId, dispatch, handleError],
  );

  const toggleAlert = useCallback(
    async ({
      deployToOrgIds,
      updateAutomationDto,
      versionId,
    }: {
      updateAutomationDto: AutomationStructureNew;
      deployToOrgIds: string[];
      versionId: string;
    }) => {
      try {
        const toSend = {
          ...updateAutomationDto,
          deployToOrgIds,
        };
        await toggle_automation(toSend);
        const shouldDeploy = deployToOrgIds.length > 0;

        if (crmOrgId) {
          dispatch(
            toggleAlertAction({
              automationToUpdate: toSend,
              crmOrgId,
              versionIdToUpdate: versionId,
              optimisticStatus: shouldDeploy
                ? DeployStatusForTable.Deployed
                : updateAutomationDto.status,
            }),
          );
        }
      } catch (e) {
        return handleError(
          e,
          updateAutomationDto.isActive ? FailedAction.activate : FailedAction.deactivate,
        );
      }
    },
    [toggle_automation, crmOrgId, dispatch, handleError],
  );

  const deployAlert = useCallback(
    async ({
      automationId,
      versionId,
      deployToOrgIds,
      deployAutomationDto,
    }: {
      deployAutomationDto: AutomationStructureNew;
      automationId: string;
      versionId: string;
      deployToOrgIds: string[];
    }) => {
      try {
        deploy_automation({
          automationId,
          versionId,
          type: deployAutomationDto.type,
          deployToOrgIds,
        });
        trackDeployment(DEPLOY_TYPE_VALUES.ALERTS);
        if (crmOrgId) {
          dispatch(
            updateAlertDeployStatus({
              crmOrgId,
              automationId,
              versionId,
              optimisticStatus: DeployStatusForTable.Deployed,
            }),
          );
        }
        //when deploy is finished, a web socket will be sent in order to trigger "requestRefresh"
      } catch (e) {
        return handleError(e, FailedAction.deploy);
      }
    },
    [deploy_automation, crmOrgId, dispatch, handleError, trackDeployment],
  );

  return { editAlert, createAlert, deleteAlert, deployAlert, toggleAlert };
};

export default useAlerts;
